import React, { useState, useEffect } from 'react';
import '../pages-styles/football-agency.css';
import PlayerSubmissionForm from '../components/player-submission-form';
import ClubEnquiryForm from '../components/club-enquiry-form';
import { usePageMeta } from '../hooks/usePageMeta';

// Material Design Icons
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import SchoolIcon from '@mui/icons-material/School';
import HandshakeIcon from '@mui/icons-material/Handshake';

function FootballAgency() {
  usePageMeta(
    'Professional Football Agency Services | Magbity Agency',
    'Magbity Agency - Independent professional football agency providing player representation, scouting, and advisory services in compliance with FIFA FFAR and FA regulations.'
  );
  
  const [activeTab, setActiveTab] = useState('overview');
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flipped, setFlipped] = useState({});

  // Toggle card flip
  const toggleFlip = (cardId) => {
    setFlipped(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  // Set activeTab based on URL hash
  useEffect(() => {
    const hash = window.location.hash.slice(1) || 'overview';
    setActiveTab(hash);

    const handleHashChange = () => {
      const newHash = window.location.hash.slice(1) || 'overview';
      setActiveTab(newHash);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Fetch Football Agency Services from backend
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const apiUrl = process.env.REACT_APP_API_URL || 'https://admin.magbityagencyltd.com/api';
        const response = await fetch(`${apiUrl}/football-agency/`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch services: HTTP ${response.status}`);
        }
        
        const data = await response.json();
        // Convert array to object indexed by service_type
        const servicesObject = {};
        if (Array.isArray(data)) {
          data.forEach(service => {
            servicesObject[service.service_type] = service;
          });
        }
        setServices(servicesObject);
        setError(null);
      } catch (err) {
        console.error('Error fetching football agency services:', err);
        setError(err.message);
        // Fallback to empty services object
        setServices({});
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  return (
    <div className="football-agency-page">
      {loading && (
        <div style={{textAlign: 'center', padding: '40px'}}>
          <p style={{color: '#e0e0e0', fontSize: '1.1rem'}}>Loading football agency services...</p>
        </div>
      )}

      {error && (
        <div style={{
          background: '#ff6b6b',
          color: 'white',
          padding: '20px',
          borderRadius: '8px',
          textAlign: 'center',
          margin: '20px 0'
        }}>
          <p><strong>Error loading services:</strong> {error}</p>
          <p style={{fontSize: '0.9rem', marginTop: '10px'}}>Using default content</p>
        </div>
      )}

      {!loading && (
        <>
        <section className="hero-banner">
          <div className="hero-overlay"></div>
          <div className="hero-content">
            <h1>Football Agency Services</h1>
            <p>Magbity Agency is an independent professional football agency providing representation, intermediary, scouting, and advisory services strictly within the limits of applicable football regulations and the laws of England and Wales.</p>
            <div className="hero-cta-buttons">
              <a href="#players" className="cta-btn cta-btn-primary">For Players</a>
              <a href="#clubs" className="cta-btn cta-btn-secondary">For Clubs</a>
            </div>
          </div>
        </section>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
        <section className="tab-content">
          <h2>Our Football Agency Services</h2>
          <p>All services are provided solely at the discretion of Magbity Agency, only where lawful, and only pursuant to a valid written agreement. No service is implied, assumed, promised, or guaranteed.</p>
          
          <div className="services-grid">
            <div className="service-card">
              <div 
                className={`card-image-wrapper ${flipped['player-rep'] ? 'flipped' : ''}`}
                onClick={() => toggleFlip('player-rep')}
              >
                <div className="card-image">
                  <img 
                    src="https://i.pinimg.com/736x/15/5f/35/155f35031f9db7f0918f8ed2b42e827e.jpg" 
                    alt="Professional Player Representation" 
                    className="card-image-icon" 
                  />
                </div>
              </div>
              <div className="card-content">
                <h3>Professional Player Representation</h3>
                <ul>
                  <li>Career strategy and professional guidance</li>
                  <li>Representation in football-related negotiations</li>
                  <li>Liaison with clubs, academies, leagues, and authorities</li>
                  <li>Support during registration and transfer processes</li>
                  <li>Regulatory-compliant representation</li>
                </ul>
              </div>
            </div>
            
            <div className="service-card">
              <div 
                className={`card-image-wrapper ${flipped['scouting'] ? 'flipped' : ''}`}
                onClick={() => toggleFlip('scouting')}
              >
                <div className="card-image">
                  <img 
                    src="https://i.pinimg.com/1200x/e0/2c/90/e02c90de3529ac9866b8c1eb6ca082bb.jpg" 
                    alt="Scouting & Talent Identification" 
                    className="card-image-icon" 
                  />
                </div>
              </div>
              <div className="card-content">
                <h3>Scouting & Talent Identification</h3>
                <ul>
                  <li>Identification and assessment of football talent</li>
                  <li>Technical, tactical, and physical evaluation</li>
                  <li>Preparation and presentation of player profiles</li>
                  <li>Market assessment and player profiling</li>
                  <li>No guarantee of trials or contracts</li>
                </ul>
              </div>
            </div>

            <div className="service-card">
              <div 
                className={`card-image-wrapper ${flipped['advisory'] ? 'flipped' : ''}`}
                onClick={() => toggleFlip('advisory')}
              >
                <div className="card-image">
                  <img 
                    src="https://i.pinimg.com/1200x/9c/d6/41/9cd641497c8fae0e226a162c2cdc2b3b.jpg" 
                    alt="Career Advisory & Strategic Guidance" 
                    className="card-image-icon" 
                  />
                </div>
              </div>
              <div className="card-content">
                <h3>Career Advisory & Strategic Guidance</h3>
                <ul>
                  <li>Career planning and development pathways</li>
                  <li>Professional conduct and long-term positioning</li>
                  <li>Advisory support on football-related decisions</li>
                  <li>Non-binding, non-guaranteed guidance</li>
                  <li>Subject to external factors beyond our control</li>
                </ul>
              </div>
            </div>

            <div className="service-card">
              <div 
                className={`card-image-wrapper ${flipped['negotiation'] ? 'flipped' : ''}`}
                onClick={() => toggleFlip('negotiation')}
              >
                <div className="card-image">
                  <img 
                    src="https://i.pinimg.com/736x/99/40/ff/9940ff709896e5194ce661800d86c50b.jpg" 
                    alt="Contractual & Negotiation Support" 
                    className="card-image-icon" 
                  />
                </div>
              </div>
              <div className="card-content">
                <h3>Contractual & Negotiation Support</h3>
                <ul>
                  <li>Support negotiations between players and clubs</li>
                  <li>Assistance in reviewing contractual terms</li>
                  <li>Act as intermediary in permitted transactions</li>
                  <li>No guarantee of contractual terms or remuneration</li>
                  <li>Regulatory-compliant only</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="important-note">
            <strong>⚠️ Important Notice:</strong> Magbity Agency does NOT guarantee trials, contracts, transfers, registrations, selection, playing time, earnings, or any career advancement. All final decisions rest exclusively with players, clubs, and governing bodies.
          </div>
        </section>
      )}

      {/* Players Tab */}
      {activeTab === 'players' && (
        <section className="tab-content">
          <h2>Player Representation Services</h2>
          <p>We work with football players on a professional, strategic, and regulatory-compliant basis. All services are provided only with a valid written agreement.</p>
          
          <div className="services-list">
            <div className="service-item">
              <div className="item-image-wrapper">
                <div className="item-image career-image"></div>
              </div>
              <div className="item-content">
                <h3>Professional Player Representation</h3>
                <p>We advise players on career positioning and progression, represent players in football-related negotiations where legally permitted, and support lawful employment and transfer processes. Representation does not constitute employment or any guaranteed opportunity.</p>
              </div>
            </div>
            <div className="service-item">
              <div className="item-image-wrapper">
                <div className="item-image contract-image"></div>
              </div>
              <div className="item-content">
                <h3>Career Strategy & Advisory Services</h3>
                <p>Structured advisory support including career planning, development pathways, professional conduct guidance, and positioning for long-term progression. All advice is non-binding and subject to external factors beyond our control.</p>
              </div>
            </div>
            <div className="service-item">
              <div className="item-image-wrapper">
                <div className="item-image trial-image"></div>
              </div>
              <div className="item-content">
                <h3>Scouting & Talent Assessment</h3>
                <p>Identification and assessment of your football talent, including technical, tactical, and physical evaluation. We present player profiles to clubs and football organisations. No guarantee of trials, contracts, or opportunities.</p>
              </div>
            </div>
            <div className="service-item">
              <div className="item-image-wrapper">
                <div className="item-image transfer-image"></div>
              </div>
              <div className="item-content">
                <h3>Contractual & Negotiation Support</h3>
                <p>Where permitted by regulation, we support negotiations between you and clubs and assist in reviewing contractual terms. We do not guarantee contractual terms, remuneration, or successful negotiations.</p>
              </div>
            </div>
            <div className="service-item">
              <div className="item-image-wrapper">
                <div className="item-image welfare-image"></div>
              </div>
              <div className="item-content">
                <h3>Regulatory Compliance & Support</h3>
                <p>We operate in full compliance with applicable football regulations, FIFA FFAR, and FA rules. We advise on regulatory requirements affecting your representation. Regulatory approvals are never guaranteed.</p>
              </div>
            </div>
          </div>

          <div className="guarantee-notice">
            <h3>⚠️ Absolute No-Guarantee Statement</h3>
            <p>Magbity Agency expressly states that it does NOT guarantee:</p>
            <ul>
              <li>Trials, contracts, transfers, registrations, or employment</li>
              <li>Selection by clubs or national teams</li>
              <li>Playing time, earnings, or career advancement</li>
              <li>Immigration, visas, work permits, or regulatory approval</li>
            </ul>
            <p><strong>Any expectation of guaranteed outcomes is unfounded and rejected.</strong></p>
          </div>

          <div className="form-section">
            <h3>Submit Your Player Profile</h3>
            <PlayerSubmissionForm />
          </div>
        </section>
      )}

      {/* Clubs Tab */}
      {activeTab === 'clubs' && (
        <section className="tab-content">
          <h2>Club Services</h2>
          <p>Magbity Agency supports clubs with player sourcing, market assessment, recruitment support, and intermediary services. All activities remain subject to football regulations and our sole discretion.</p>
          
          <div className="services-list">
            <div className="service-item">
              <div className="item-image-wrapper">
                <div className="item-image sourcing-image"></div>
              </div>
              <div className="item-content">
                <h3>Club Liaison & Introductions</h3>
                <p>At our sole discretion, we introduce player profiles to clubs or football entities and facilitate lawful communication and information exchange. We do not influence, control, or guarantee club decisions.</p>
              </div>
            </div>
            <div className="service-item">
              <div className="item-image-wrapper">
                <div className="item-image availability-image"></div>
              </div>
              <div className="item-content">
                <h3>Scouting, Talent Identification & Market Assessment</h3>
                <p>We conduct professional identification and assessment of players, technical and tactical profiling, and presentation of player information to clubs. Scouting activities do not create obligations for clubs or guarantee outcomes.</p>
              </div>
            </div>
            <div className="service-item">
              <div className="item-image-wrapper">
                <div className="item-image loan-image"></div>
              </div>
              <div className="item-content">
                <h3>Contractual & Negotiation Support</h3>
                <p>Where permitted by regulation, we support negotiations and assist in reviewing football employment terms. We act as intermediary in permitted transactions. No guarantees of terms, remuneration, or successful outcomes.</p>
              </div>
            </div>
            <div className="service-item">
              <div className="item-image-wrapper">
                <div className="item-image market-image"></div>
              </div>
              <div className="item-content">
                <h3>Service Discretion & Limitation</h3>
                <p>We retain full discretion over the scope and continuation of services. We are not obliged to act on every opportunity or enquiry. Services may be limited or withdrawn where regulatory, reputational, or legal risk arises.</p>
              </div>
            </div>
            <div className="service-item">
              <div className="item-image-wrapper">
                <div className="item-image negotiation-services"></div>
              </div>
              <div className="item-content">
                <h3>No Third-Party Control</h3>
                <p>Magbity Agency is not responsible for the actions or decisions of football clubs, leagues, governing bodies, coaches, scouts, or third-party intermediaries. We cannot influence or guarantee their decisions.</p>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Club Enquiry Form</h3>
            <ClubEnquiryForm />
          </div>
        </section>
      )}

      {/* Compliance Tab */}
      {activeTab === 'compliance' && (
        <section className="tab-content">
          <h2>Regulatory Framework & Compliance</h2>
          <p>All football agency services are delivered in strict accordance with applicable regulations and the laws of England and Wales. Regulatory compliance overrides all expectations, requests, or personal interests.</p>
          
          <div className="compliance-list">
            <div className="compliance-item">
              <h3>FIFA Football Agent Regulations (FFAR)</h3>
              <p>Full compliance with FIFA's global football agent standards, licensing requirements, and international regulations governing football agency services.</p>
            </div>
            <div className="compliance-item">
              <h3>FA Football Agent Regulations</h3>
              <p>Complete adherence to The Football Association regulations in England & Wales, including registration, conduct standards, and professional requirements.</p>
            </div>
            <div className="compliance-item">
              <h3>Youth & Minor Protection</h3>
              <p>For players under 18: Services provided only with verified written parental or guardian consent. No fees or commissions charged where prohibited by regulation. All activity complies with child protection standards.</p>
            </div>
            <div className="compliance-item">
              <h3>Data Protection & Privacy</h3>
              <p>Full compliance with UK GDPR, data protection laws, and privacy regulations. All personal data collected is processed lawfully and stored securely.</p>
            </div>
            <div className="compliance-item">
              <h3>Professional Conduct Standards</h3>
              <p>Magbity Agency operates to the highest standards of professional ethics, integrity, and conduct in all football-related activities.</p>
            </div>
          </div>

          <div className="governing-law">
            <h3>Governing Law & Jurisdiction</h3>
            <p>All football agency services are governed by the laws of England and Wales. Any disputes shall be handled exclusively within UK jurisdiction. In the event of conflict between regulatory requirements and any request or expectation, regulatory and legal compliance prevails.</p>
          </div>
        </section>
      )}
      </>
      )}
    </div>
  );
}

export default FootballAgency;
